import { DateTime } from 'luxon';

/**
 * Alle Termine dieses Features rechnen in Berliner Zeit — nie in der Serverzeit.
 *
 * Der Server läuft in UTC (kein `TZ` in Dockerfile, .env oder CI), und der BaseQL-`DateTime`-
 * Scalar liest mit `DateTime.fromISO(value)` ohne `{ setZone: true }`. Ein als Mitternacht
 * Berlin gespeichertes Datum kommt deshalb als `2026-09-20T22:00Z` zurück und formatiert sich
 * als der **Vortag**. Genauso ist `DateTime.now().hour` im Sommer zwei Stunden zu früh — eine
 * 12-Uhr-Grenze darauf wäre schlicht falsch.
 *
 * Deshalb gilt für dieses Feature ausnahmslos: kein `.hour`, kein `.weekday`, kein `toFormat`
 * ohne vorheriges `.setZone(BERLIN)`.
 */
export const BERLIN = 'Europe/Berlin';

/** Bis zu dieser Uhrzeit am Werktag vor dem Abfahrtstag zählt eine Anfrage noch für ihn. */
export const CUTOFF_HOUR = 12;

/**
 * Notbremse für jede Suchschleife über Kalendertage. Der real längste Sprung ist die
 * Betriebsruhe zusammen mit den Weihnachtsfeiertagen und einer Zone, die nur an einem Wochentag
 * fährt — rund 22 Tage. Wird die Grenze erreicht, stimmt etwas an den Daten nicht; dann soll es
 * auffallen statt still zu hängen.
 */
const MAX_DAY_SCAN = 30;

/** Ostersonntag nach der anonymen gregorianischen Formel (Meeus/Jones/Butcher). */
function easterSunday(year: number): DateTime {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return DateTime.fromObject({ year, month, day }, { zone: BERLIN });
}

const holidayCache = new Map<number, Set<string>>();

/**
 * Die elf gesetzlichen Feiertage in Nordrhein-Westfalen, als `yyyy-MM-dd`.
 *
 * Fronleichnam und Allerheiligen sind die beiden, die NRW von den meisten anderen Ländern
 * unterscheiden. Oster- und Pfingstsonntag stehen bewusst nicht drin: Sonntage sind ohnehin
 * keine Werktage, und ein doppelter Eintrag verschleiert nur, welche Regel gegriffen hat.
 */
export function nrwHolidaysForYear(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;

  const easter = easterSunday(year);
  const days = [
    DateTime.fromObject({ year, month: 1, day: 1 }, { zone: BERLIN }), // Neujahr
    easter.minus({ days: 2 }), // Karfreitag
    easter.plus({ days: 1 }), // Ostermontag
    DateTime.fromObject({ year, month: 5, day: 1 }, { zone: BERLIN }), // Tag der Arbeit
    easter.plus({ days: 39 }), // Christi Himmelfahrt
    easter.plus({ days: 50 }), // Pfingstmontag
    easter.plus({ days: 60 }), // Fronleichnam
    DateTime.fromObject({ year, month: 10, day: 3 }, { zone: BERLIN }), // Tag der Deutschen Einheit
    DateTime.fromObject({ year, month: 11, day: 1 }, { zone: BERLIN }), // Allerheiligen
    DateTime.fromObject({ year, month: 12, day: 25 }, { zone: BERLIN }), // 1. Weihnachtstag
    DateTime.fromObject({ year, month: 12, day: 26 }, { zone: BERLIN }), // 2. Weihnachtstag
  ];

  const set = new Set(days.map(d => d.toFormat('yyyy-MM-dd')));
  holidayCache.set(year, set);
  return set;
}

export function isNrwHoliday(date: DateTime): boolean {
  const berlin = date.setZone(BERLIN);
  return nrwHolidaysForYear(berlin.year).has(berlin.toFormat('yyyy-MM-dd'));
}

/**
 * Betriebsruhe vom 24.12. bis einschließlich 01.01. — in dieser Zeit wird weder versendet noch
 * zugestellt. Als benannte Konstanten, weil H&D das jährlich neu festlegen kann.
 */
const SHUTDOWN_FROM_DECEMBER_DAY = 24;

export function isCompanyShutdown(date: DateTime): boolean {
  const berlin = date.setZone(BERLIN);
  if (berlin.month === 12 && berlin.day >= SHUTDOWN_FROM_DECEMBER_DAY) return true;
  return berlin.month === 1 && berlin.day === 1;
}

/** Mo–Fr, kein NRW-Feiertag, keine Betriebsruhe. */
export function isWorkingDay(date: DateTime): boolean {
  const berlin = date.setZone(BERLIN);
  if (berlin.weekday > 5) return false;
  return !isNrwHoliday(berlin) && !isCompanyShutdown(berlin);
}

/** Der Tag selbst, wenn er ein Werktag ist — sonst der nächste. */
export function rollForwardToWorkingDay(date: DateTime): DateTime {
  let day = date.setZone(BERLIN).startOf('day');
  for (let i = 0; i < MAX_DAY_SCAN; i += 1) {
    if (isWorkingDay(day)) return day;
    day = day.plus({ days: 1 });
  }
  throw new Error(`Kein Werktag innerhalb von ${MAX_DAY_SCAN} Tagen nach ${String(date.toISO())} gefunden.`);
}

export function previousWorkingDay(date: DateTime): DateTime {
  let day = date.setZone(BERLIN).startOf('day').minus({ days: 1 });
  for (let i = 0; i < MAX_DAY_SCAN; i += 1) {
    if (isWorkingDay(day)) return day;
    day = day.minus({ days: 1 });
  }
  throw new Error(`Kein Werktag innerhalb von ${MAX_DAY_SCAN} Tagen vor ${String(date.toISO())} gefunden.`);
}

/**
 * `count` Werktage nach `date`. Wochenenden, Feiertage und Betriebsruhe zählen nicht mit —
 * genau das meint die Spec mit „Transportzeit in Stunden, real in Tagen": aus Donnerstag plus
 * 48 h wird Montag, weil Samstag und Sonntag nicht gefahren wird.
 */
export function addWorkingDays(date: DateTime, count: number): DateTime {
  if (count < 0) throw new Error('addWorkingDays erwartet eine nicht-negative Anzahl.');
  let day = date.setZone(BERLIN).startOf('day');
  for (let i = 0; i < count; i += 1) {
    day = rollForwardToWorkingDay(day.plus({ days: 1 }));
  }
  return day;
}
